/**
 * OpsPanel — operational visibility for the admin:
 *   1. Background-scheduler health (3 jobs: lead backup, weekly report, SEO digest)
 *   2. Anonymous lite-assessment history (one row per browser device_id)
 *   3. Anonymous in-progress drafts (visitors mid-assessment)
 *
 * Read-only. No mutations. Use the existing endpoints exposed by
 * `scheduler_health.py` and `admin_routes.py`.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Activity, CheckCircle2, AlertTriangle, Clock, RefreshCw, Globe, FileText,
  Inbox, TrendingUp, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
};

const STATUS_BADGE = {
  ok:        { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "OK" },
  failed:    { cls: "bg-red-50 text-red-800 border-red-200", icon: AlertTriangle, label: "Failed" },
  running:   { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: RefreshCw, label: "Running" },
  never_run: { cls: "ek-surface-2 ek-text-soft ek-line", icon: Clock, label: "Never run" },
};

function SchedulerHealth() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/scheduler/health");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scheduler health load failed");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="ek-text-muted text-[13px]">Loading scheduler health…</div>;

  return (
    <div className="ek-surface border ek-line" data-testid="scheduler-health">
      <div className="flex items-center justify-between px-4 py-3 border-b ek-line ek-surface-2">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#7737F6]"/>
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase ek-text-soft">Scheduler health</div>
        </div>
        <button type="button" onClick={load} className="text-[11px] ek-text-soft hover:text-[#7737F6] inline-flex items-center gap-1" data-testid="scheduler-refresh">
          <RefreshCw size={11}/> refresh
        </button>
      </div>
      <div className="grid grid-cols-3 gap-0">
        {items.map((it) => {
          const meta = STATUS_BADGE[it.status] || STATUS_BADGE.never_run;
          const Icon = meta.icon;
          return (
            <div key={it.job} className="border-r ek-line last:border-r-0 p-4" data-testid={`job-${it.job}`}>
              <div className="text-[10.5px] font-semibold uppercase tracking-wider ek-text-muted">{it.label}</div>
              <div className="mt-2 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 border ${meta.cls}`}>
                  <Icon size={11}/> {meta.label}
                </span>
              </div>
              <div className="text-[12px] ek-text-soft mt-3 space-y-1">
                <div>
                  <span className="ek-text-muted">Last success: </span>
                  <span className="font-medium">{it.last_success_at ? fmtRel(it.last_success_at) : "—"}</span>
                </div>
                {it.last_failure_at && (
                  <div className="text-red-700 text-[11px]">
                    Last failure: {fmtRel(it.last_failure_at)}
                  </div>
                )}
                <div>
                  <span className="ek-text-muted">Next run: </span>
                  <span className="font-medium">{it.next_run_at ? fmtDT(it.next_run_at) : "—"}</span>
                </div>
                {typeof it.success_count === "number" && (
                  <div className="text-[11px] ek-text-muted">
                    {it.success_count} successes · {it.failure_count || 0} failures
                  </div>
                )}
                {it.last_error && (
                  <div className="text-[11px] text-red-700 bg-red-50 px-2 py-1 mt-2">
                    {it.last_error}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LiteHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lite-history");
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lite history load failed");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="ek-surface border ek-line" data-testid="lite-history">
      <div className="flex items-center justify-between px-4 py-3 border-b ek-line ek-surface-2">
        <div className="flex items-center gap-2">
          <Globe size={14} className="text-[#7737F6]"/>
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase ek-text-soft">Anonymous lite-assessment history</div>
          <span className="text-[11px] ek-text-muted">({rows.length})</span>
        </div>
        <button type="button" onClick={load} className="text-[11px] ek-text-soft hover:text-[#7737F6] inline-flex items-center gap-1" data-testid="history-refresh">
          <RefreshCw size={11}/> refresh
        </button>
      </div>
      {loading ? (
        <div className="p-4 ek-text-muted text-[13px]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-4 ek-text-muted text-[13px] italic">No completed lite assessments yet.</div>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.12em] ek-text-muted ek-surface-2">
              <th className="text-left px-4 py-2 font-semibold">Device</th>
              <th className="text-left px-4 py-2 font-semibold">Identified contact</th>
              <th className="text-right px-4 py-2 font-semibold">Frameworks</th>
              <th className="text-right px-4 py-2 font-semibold">Total scores</th>
              <th className="text-left px-4 py-2 font-semibold">Last activity</th>
              <th className="text-left px-4 py-2 font-semibold">Latest scores</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.device_id} className="border-b ek-line hover:bg-[#FAF7FF]">
                <td className="px-4 py-2 font-mono text-[11px] ek-text-muted">{(r.device_id || "").slice(0, 16)}…</td>
                <td className="px-4 py-2">
                  {r.identified ? (
                    <div>
                      <div className="font-medium">{r.identified.full_name || r.identified.email}</div>
                      <div className="text-[11px] ek-text-muted">{r.identified.organisation || ""}</div>
                    </div>
                  ) : (
                    <span className="ek-text-muted italic">anonymous</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{r.unique_sub_brands}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.total_scores}</td>
                <td className="px-4 py-2 ek-text-soft">{fmtRel(r.last_seen)}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(r.latest_by_sub_brand || []).map((s) => (
                      <span key={s.slug} className="inline-block text-[10.5px] ek-surface-2 ek-text-soft px-1.5 py-0.5">
                        <span className="font-semibold ek-text">{s.sub_brand}</span> {s.overall}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LiteDrafts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lite-drafts");
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lite drafts load failed");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="ek-surface border ek-line" data-testid="lite-drafts">
      <div className="flex items-center justify-between px-4 py-3 border-b ek-line ek-surface-2">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-[#7737F6]"/>
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase ek-text-soft">In-progress drafts</div>
          <span className="text-[11px] ek-text-muted">({rows.length})</span>
        </div>
        <button type="button" onClick={load} className="text-[11px] ek-text-soft hover:text-[#7737F6] inline-flex items-center gap-1" data-testid="drafts-refresh">
          <RefreshCw size={11}/> refresh
        </button>
      </div>
      {loading ? (
        <div className="p-4 ek-text-muted text-[13px]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-4 ek-text-muted text-[13px] italic">No drafts in progress — every visitor who started a lite assessment finished it.</div>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.12em] ek-text-muted ek-surface-2">
              <th className="text-left px-4 py-2 font-semibold">Framework</th>
              <th className="text-left px-4 py-2 font-semibold">Device</th>
              <th className="text-left px-4 py-2 font-semibold">Identified contact</th>
              <th className="text-right px-4 py-2 font-semibold">Progress</th>
              <th className="text-left px-4 py-2 font-semibold">Last update</th>
              <th className="text-left px-4 py-2 font-semibold">Started</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.device_id}_${r.slug}`} className="border-b ek-line hover:bg-[#FAF7FF]">
                <td className="px-4 py-2"><span className="font-semibold">{r.sub_brand}</span></td>
                <td className="px-4 py-2 font-mono text-[11px] ek-text-muted">{(r.device_id || "").slice(0, 16)}…</td>
                <td className="px-4 py-2">
                  {r.identified ? (
                    <div>
                      <div className="font-medium">{r.identified.full_name || r.identified.email}</div>
                      <div className="text-[11px] ek-text-muted">{r.identified.organisation || ""}</div>
                    </div>
                  ) : (
                    <span className="ek-text-muted italic">anonymous</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  <div className="font-semibold">
                    {r.answers_count}/{r.total_indicators || "—"}
                  </div>
                  {r.completeness_pct != null && (
                    <div className="text-[11px] ek-text-muted">{r.completeness_pct}%</div>
                  )}
                </td>
                <td className="px-4 py-2 ek-text-soft">{fmtRel(r.updated_at)}</td>
                <td className="px-4 py-2 ek-text-muted text-[11px]">{fmtRel(r.started_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EnquiriesFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enquiries/summary");
      setData(data);
    } catch (e) {
      toast.error("Could not load enquiries summary");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Each channel drills through to its leads tab (newest-first, so this month is on top).
  const TAB_FOR = {
    discovery_enquiries: "Contacts",
    brochure_downloads: "Brochure requests",
    trial_requests: "Trials",
    assessments_started: "Assessment leads",
    preview_requests: "Preview leads",
  };
  const openChannel = (key) => {
    const tab = TAB_FOR[key];
    if (tab) window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: tab }));
  };

  const Delta = ({ pct }) => {
    if (pct === null || pct === undefined) return <span className="text-[11px] ek-text-muted">new</span>;
    const up = pct >= 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${up ? "text-emerald-600" : "text-red-500"}`}>
        {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{Math.abs(pct)}%
      </span>
    );
  };

  return (
    <div className="ek-surface border ek-line rounded-lg p-4" data-testid="ops-enquiries-funnel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Inbox size={16} className="text-[var(--e360-primary,#7737F6)]" />
          <h3 className="text-[13px] font-semibold ek-text">Enquiries this month</h3>
          {data?.month && <span className="text-[11px] ek-text-muted">· {data.month}</span>}
        </div>
        <button onClick={load} disabled={loading} data-testid="ops-enquiries-refresh"
          className="inline-flex items-center gap-1 text-[11px] ek-text-muted hover:opacity-70 transition-opacity">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      {loading && !data ? (
        <div className="text-[12px] ek-text-muted py-4">Loading…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {data.channels.map((c) => (
              <button key={c.key} type="button" onClick={() => openChannel(c.key)}
                data-testid={`ops-enquiry-${c.key}`} title={`View ${c.label.toLowerCase()}`}
                className="ek-surface-2 border ek-line rounded-md p-3 text-left hover:border-[var(--e360-primary,#7737F6)] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--e360-primary,#7737F6)]/40 transition-all cursor-pointer group">
                <div className="text-[24px] font-semibold ek-text leading-none tabular-nums">{c.this_month}</div>
                <div className="text-[11px] ek-text-muted mt-1.5 leading-tight group-hover:text-[var(--e360-primary,#7737F6)] transition-colors">{c.label}</div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <Delta pct={c.delta_pct} />
                  <span className="text-[10.5px] ek-text-muted">vs {c.last_month} last mo</span>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t ek-line flex items-center gap-2 text-[12px] ek-text-soft flex-wrap">
            <span className="font-semibold ek-text">{data.total.this_month}</span> total enquiries this month
            <Delta pct={data.total.delta_pct} />
            <span className="text-[10.5px] ek-text-muted ml-auto">Sourced live from form submissions</span>
          </div>
        </>
      ) : null}
    </div>
  );
}


export default function OpsPanel() {
  return (
    <div className="space-y-5" data-testid="ops-panel">
      <EnquiriesFunnel />
      <SchedulerHealth />
      <LiteHistory />
      <LiteDrafts />
    </div>
  );
}
